import { useTraderText } from '../context/TraderLanguageContext';
/**
 * Watchlist Content Component (Extracted for scroll stability)
 * This component is defined outside MarketWatchlist to prevent remounting on WebSocket updates
 */
import React, { memo, useRef, useEffect, useCallback, useMemo } from 'react';
import { Search, Star, ChevronDown, ChevronUp, RefreshCw, Wifi, WifiOff, Clock } from 'lucide-react';
import InstrumentIcon from './InstrumentIcon';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Trading hours configuration - MUST MATCH backend trading_sessions.py MARKET_HOURS
const TRADING_HOURS = {
  forex: { 
    hours: 'Mon-Fri',
    fullDesc: 'Monday 01:00 - Friday 23:59 UTC',
    isOpen: () => {
      const now = new Date();
      const day = now.getUTCDay();
      const hour = now.getUTCHours();
      const min = now.getUTCMinutes();
      const time = hour + min/60;
      if (day === 0) return false; // Sunday
      if (day === 6) return false; // Saturday
      if (day === 1 && time < 1) return false; // Monday before 01:00
      if (day === 5 && time > 23.98) return false; // Friday after 23:59
      return true;
    }
  },
  crypto: { 
    hours: '24/7',
    fullDesc: 'Always open - 24 hours, 7 days',
    isOpen: () => true
  },
  stocks_us: { 
    hours: '14:30-21:00 UTC',
    fullDesc: 'Mon-Fri 14:30 - 21:00 UTC',
    isOpen: () => {
      const now = new Date();
      const day = now.getUTCDay();
      if (day === 0 || day === 6) return false;
      const hour = now.getUTCHours();
      const min = now.getUTCMinutes();
      const time = hour + min/60;
      return time >= 14.5 && time < 21;
    }
  },
  stocks: { 
    hours: '14:30-21:00 UTC',
    fullDesc: 'Mon-Fri 14:30 - 21:00 UTC (US Market)',
    isOpen: () => {
      const now = new Date();
      const day = now.getUTCDay();
      if (day === 0 || day === 6) return false;
      const hour = now.getUTCHours();
      const min = now.getUTCMinutes();
      const time = hour + min/60;
      return time >= 14.5 && time < 21;
    }
  },
  stocks_bist: { 
    hours: '07:00-15:00 UTC',
    fullDesc: 'Mon-Fri 07:00 - 15:00 UTC (Istanbul)',
    isOpen: () => {
      const now = new Date();
      const day = now.getUTCDay();
      if (day === 0 || day === 6) return false;
      const hour = now.getUTCHours();
      return hour >= 7 && hour < 15;
    }
  },
  bist: { 
    hours: '07:00-15:00 UTC',
    fullDesc: 'Mon-Fri 07:00 - 15:00 UTC (Istanbul)',
    isOpen: () => {
      const now = new Date();
      const day = now.getUTCDay();
      if (day === 0 || day === 6) return false;
      const hour = now.getUTCHours();
      return hour >= 7 && hour < 15;
    }
  },
  commodities: { 
    hours: 'Sun 23:00 - Fri 22:00',
    fullDesc: 'Sunday 23:00 UTC - Friday 22:00 UTC',
    isOpen: () => {
      const now = new Date();
      const day = now.getUTCDay();
      const hour = now.getUTCHours();
      if (day === 6) return false; // Saturday - closed
      if (day === 0 && hour < 23) return false; // Sunday before 23:00
      if (day === 5 && hour >= 22) return false; // Friday after 22:00
      return true;
    }
  },
  indices: { 
    hours: 'Sun 23:00 - Fri 22:00',
    fullDesc: 'Sunday 23:00 UTC - Friday 22:00 UTC',
    isOpen: () => {
      const now = new Date();
      const day = now.getUTCDay();
      const hour = now.getUTCHours();
      if (day === 6) return false; // Saturday - closed
      if (day === 0 && hour < 23) return false; // Sunday before 23:00
      if (day === 5 && hour >= 22) return false; // Friday after 22:00
      return true;
    }
  },
};

const getMarketStatus = (category) => {
  const config = TRADING_HOURS[category] || TRADING_HOURS.forex;
  return {
    isOpen: config.isOpen(),
    hours: config.hours,
    fullDesc: config.fullDesc
  };
};

const WatchlistContent = memo(({
  isDark,
  t,
  search,
  setSearch,
  filter,
  setFilter,
  filters,
  oneClickTrading,
  setOneClickTrading,
  loading,
  filteredInstruments,
  isConnected,
  selectedInstrument,
  favorites,
  toggleFavorite,
  handleSelectInstrument,
  handleInstrumentDoubleClick,
  handleQuickTrade,
  formatPrice,
  showHeader = true,
  onLoadMore,
}) => {
  const tx = useTraderText();
  // Scroll position ref to preserve across re-renders
  const scrollContainerRef = useRef(null);
  const scrollPositionRef = useRef(0);

  // Save scroll position on scroll and load more if near bottom
  const handleScroll = useCallback(() => {
    if (scrollContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
      scrollPositionRef.current = scrollTop;
      if (scrollHeight - scrollTop - clientHeight < 250) {
        onLoadMore?.();
      }
    }
  }, [onLoadMore]);

  // Restore scroll position after updates (using layoutEffect for synchronous update)
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container && scrollPositionRef.current > 0) {
      requestAnimationFrame(() => {
        if (container) {
          container.scrollTop = scrollPositionRef.current;
        }
      });
    }
  }, [filteredInstruments]);

  return (
    <div className="flex flex-col h-full">
      {/* Search */}
      <div className="p-3 flex-shrink-0">
        <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${
          isDark ? 'bg-[#1a2332] border-gray-700' : 'bg-gray-100 border-gray-200'
        }`}>
          <Search size={16} className={isDark ? 'text-gray-500' : 'text-gray-400'} />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('common.search')}
            className={`flex-1 bg-transparent text-sm outline-none ${
              isDark ? 'text-white placeholder-gray-500' : 'text-gray-900 placeholder-gray-400'
            }`}
            data-testid="market-search"
          />
        </div>
      </div>

      {/* Category Filter Dropdown */}
      <div className="px-3 pb-2 flex-shrink-0">
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className={`w-full px-3 py-2 rounded-lg border text-sm ${
            isDark 
              ? 'bg-[#1a2332] border-gray-700 text-white' 
              : 'bg-white border-gray-200 text-gray-900'
          }`}
          data-testid="category-filter"
        >
          {filters.map((f) => (
            <option key={f.id} value={f.id}>
              {tx(f.label)}
            </option>
          ))}
        </select>
      </div>

      {/* 1-Click Trading Toggle */}
      <div className={`mx-3 px-3 py-2 flex items-center justify-between rounded-lg border ${
        isDark ? 'border-gray-700 bg-[#1a2332]' : 'border-gray-200 bg-gray-50'
      }`}>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400">⚡</span>
          <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
            {t('trading.oneClickTrading')}
          </span>
        </div>
        <button
          onClick={() => setOneClickTrading(!oneClickTrading)}
          className={`w-10 h-5 rounded-full transition-colors relative ${
            oneClickTrading ? 'bg-cyan-500' : isDark ? 'bg-gray-700' : 'bg-gray-300'
          }`}
          data-testid="one-click-toggle"
        >
          <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform shadow ${
            oneClickTrading ? 'translate-x-5' : 'translate-x-0.5'
          }`} />
        </button>
      </div>

      {/* Instrument Count & WebSocket Status */}
      <div className={`px-3 py-2 flex items-center justify-between flex-shrink-0 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
        <span className="text-xs font-medium">{tx("Market (")}{loading ? '...' : filteredInstruments.length}) ↕
        </span>
        <div className="flex items-center gap-3 text-xs">
          <span>{t('trading.sell')}</span>
          <span>{t('trading.buy')}</span>
          {isConnected ? (
            <Wifi size={12} className="text-green-500" />
          ) : (
            <WifiOff size={12} className="text-orange-500" />
          )}
        </div>
      </div>

      {/* Instrument List - Scrollable */}
      <div 
        ref={scrollContainerRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto min-h-0"
        style={{ overscrollBehavior: 'contain' }}
        data-testid="instrument-list-container"
      >
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <RefreshCw size={20} className="animate-spin text-cyan-500" />
          </div>
        ) : filteredInstruments.length === 0 ? (
          <div className={`text-center py-8 text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{tx("No instruments found ")}</div>
        ) : (
          filteredInstruments.map((inst) => (
            <div
              key={inst.id || inst.symbol}
              onClick={() => handleSelectInstrument(inst)}
              onDoubleClick={() => handleInstrumentDoubleClick(inst)}
              onKeyDown={e=>{if(e.key==='Enter'){e.preventDefault();handleInstrumentDoubleClick(inst);}}}
              tabIndex={0}
              className={`mt5-quote-row px-3 py-2 cursor-pointer border-b transition-colors ${
                selectedInstrument?.symbol === inst.symbol
                  ? isDark 
                    ? 'bg-cyan-500/10 border-cyan-500/30' 
                    : 'bg-blue-50 border-blue-200'
                  : isDark
                    ? 'border-gray-800/50 hover:bg-gray-800/30'
                    : 'border-gray-100 hover:bg-gray-50'
              }`}
              data-testid={`instrument-${inst.symbol?.replace('/', '-')}`}
            >
              <div className="flex items-center justify-between">
                {/* Left side: Icon, Symbol, Name */}
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <button
                    onClick={(e) => toggleFavorite(inst.symbol, e)}
                    data-testid={`instrument-favorite-${inst.symbol.replace('/', '-')}`}
                    aria-label={`Favorite ${inst.symbol}`}
                    className={`flex-shrink-0 ${favorites.includes(inst.symbol) ? 'text-yellow-400' : 'text-gray-500'}`}
                  >
                    <Star size={12} fill={favorites.includes(inst.symbol) ? 'currentColor' : 'none'} />
                  </button>
                  <InstrumentIcon 
                    symbol={inst.symbol} 
                    category={inst.category} 
                    tradingview_symbol={inst.tradingview_symbol}
                    size={18} 
                    isDark={isDark}
                  />
                  <div className="min-w-0 flex-1">
                    <div className={`font-medium text-sm truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {inst.symbol}
                    </div>
                    <div className={`text-[10px] truncate ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                      {inst.name || inst.symbol}
                    </div>
                  </div>
                </div>

                {/* Right side: Prices */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button 
                    className={`px-2 py-1 rounded text-xs font-medium min-w-[60px] text-center transition-colors ${
                      oneClickTrading
                        ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
                        : isDark
                          ? 'text-gray-300'
                          : 'text-gray-700'
                    }`}
                    onClick={(e) => handleQuickTrade(inst, 'sell', e)}
                    data-testid={`trade-sell-${inst.symbol.replace('/', '-')}`}
                    aria-label={`Sell ${inst.symbol}`}
                    title={`Sell ${inst.symbol}`}
                    disabled={!(inst.bid>0&&inst.ask>0)}
                  >
                    {formatPrice(inst.bid, inst.symbol)}
                  </button>
                  <button 
                    className={`px-2 py-1 rounded text-xs font-medium min-w-[60px] text-center transition-colors ${
                      oneClickTrading
                        ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                        : inst.change >= 0 
                          ? 'text-green-400' 
                          : 'text-red-400'
                    }`}
                    onClick={(e) => handleQuickTrade(inst, 'buy', e)}
                    data-testid={`trade-buy-${inst.symbol.replace('/', '-')}`}
                    aria-label={`Buy ${inst.symbol}`}
                    title={`Buy ${inst.symbol}`}
                    disabled={!(inst.bid>0&&inst.ask>0)}
                  >
                    {formatPrice(inst.ask, inst.symbol)}
                  </button>
                </div>
              </div>
              
              {/* Price details row */}
              <div className="flex items-center justify-between mt-1 pl-7">
                <div className={`flex items-center gap-1 text-[10px] ${
                  inst.change >= 0 ? 'text-green-400' : 'text-red-400'
                }`}>
                  {inst.change >= 0 ? '▲' : '▼'}
                  {Math.abs(inst.change).toFixed(2)}%
                  {inst.quote_source?.includes('simulated')&&<span data-testid={`quote-source-${inst.symbol.replace('/', '-')}`} title={tx("Simulated engine quote — not an exchange fill")} className="ml-1 text-[9px] text-amber-500">{tx("SIM")}</span>}
                  {/* Market Status Indicator */}
                  {(() => {
                    const status = inst.is_virtual ? {isOpen:inst.session_status==='open',hours:inst.simulation_market,fullDesc:inst.session_status||'Waiting for session status'} : getMarketStatus(inst.category);
                    return (
                      <span 
                        className={`ml-1 ${status.isOpen ? 'text-green-400' : 'text-gray-500'}`}
                        title={tx(status.fullDesc)}
                      >
                        {tx(status.isOpen ? '● Open' : '○ Closed')}
                      </span>
                    );
                  })()}
                </div>
                <div 
                  className={`flex items-center gap-1 text-[10px] cursor-help ${isDark ? 'text-gray-500' : 'text-gray-400'}`}
                  title={tx(TRADING_HOURS[inst.category]?.fullDesc || 'Trading Hours')}
                >
                  <Clock size={9} />
                  {tx(TRADING_HOURS[inst.category]?.hours || '24/5')}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
});

WatchlistContent.displayName = 'WatchlistContent';

export default WatchlistContent;
